import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, download } from '../api/client';
import { useRecommendations } from '../api/queries';
import type { PriceRecommendation } from '../api/types';
import { STATUS } from '../charts/palette';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { Delta } from '../components/Delta';
import { Empty, Failed, Loading } from '../components/States';
import { StatTile } from '../components/StatTile';
import { count, money } from '../utils/format';

type Filter = 'all' | 'decrease' | 'increase';

export function RecommendationsPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const recommendations = useRecommendations(60);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function runExport() {
    setExportError(null);
    setExporting(true);

    try {
      await download('/recommendations/export.csv', 'price-moves.csv');
    } catch (cause) {
      setExportError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setExporting(false);
    }
  }

  const all: PriceRecommendation[] = recommendations.data ?? [];
  const decreases = all.filter((item) => item.direction === 'decrease');
  const increases = all.filter((item) => item.direction === 'increase');
  const shown = filter === 'all' ? all : filter === 'decrease' ? decreases : increases;

  const winBack = decreases.reduce((sum, item) => sum + Math.abs(item.deltaEur), 0);
  const marginGain = increases.reduce((sum, item) => sum + item.deltaEur, 0);

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">Price moves</h1>
          <p className="page__lede">
            Suggested prices, both directions: undercut the shop that beats you, or claim the margin you are giving
            away while already cheapest. Each suggestion respects the configured price floor.
          </p>
        </div>
        <div className="row">
          <button type="button" className="button" onClick={runExport} disabled={exporting}>
            {exporting ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
      </header>

      {exportError && (
        <div className="login__error" role="alert">
          {exportError}
        </div>
      )}

      {recommendations.isError ? (
        <Failed error={recommendations.error} />
      ) : recommendations.isLoading ? (
        <Loading label="Computing recommendations…" />
      ) : all.length === 0 ? (
        <Empty>
          <div>Nothing to change yet.</div>
          <div className="muted">Recommendations need at least one successful scan with competitor offers.</div>
        </Empty>
      ) : (
        <>
          <div className="grid grid--tiles">
            <StatTile
              label="Cuts suggested"
              value={count(decreases.length)}
              foot={`${money(winBack)} of price cuts to regain the lead`}
              accent={STATUS.critical}
            />
            <StatTile
              label="Raises suggested"
              value={count(increases.length)}
              foot={`${money(marginGain)} of margin available`}
              accent={STATUS.good}
            />
            <StatTile label="Total suggestions" value={count(all.length)} foot="ordered by size of the gap" />
          </div>

          <Card
            action={
              <div className="row" style={{ gap: 6 }}>
                {(['all', 'decrease', 'increase'] as Filter[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`button${filter === option ? ' button--primary' : ''}`}
                    style={{ padding: '5px 11px', fontSize: 12 }}
                    onClick={() => setFilter(option)}
                  >
                    {option === 'all' ? 'All' : option === 'decrease' ? 'Cuts' : 'Raises'}
                  </button>
                ))}
              </div>
            }
            title="Suggestions"
            note={`${count(shown.length)} shown`}
          >
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Move</th>
                    <th className="num">Now</th>
                    <th className="num">Suggested</th>
                    <th className="num">Change</th>
                    <th className="num">Market low</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((item) => (
                    <tr key={item.shopProductId}>
                      <td>
                        <Link to={`/products/${item.shopProductId}`} className="cell-product">
                          {item.imageUrl && (
                            <img className="cell-product__img" src={item.imageUrl} alt="" loading="lazy" />
                          )}
                          <span>
                            <span className="cell-product__name">{item.name}</span>
                            <div className="cell-product__meta">{item.ean ?? 'no EAN'}</div>
                          </span>
                        </Link>
                      </td>
                      <td>
                        <Badge
                          label={item.direction === 'decrease' ? 'Cut price' : 'Raise price'}
                          color={item.direction === 'decrease' ? STATUS.critical : STATUS.good}
                        />
                      </td>
                      <td className="num">{money(item.ourPrice)}</td>
                      <td className="num" style={{ fontWeight: 600 }}>
                        {money(item.recommendedPrice)}
                      </td>
                      <td className="num">
                        <Delta value={item.deltaPct} lowerIsBetter={item.direction === 'decrease'} />
                        <div className="cell-product__meta">{money(item.deltaEur)}</div>
                      </td>
                      <td className="num secondary">
                        {money(item.minPrice)}
                        <div className="cell-product__meta">{item.cheapestShop ?? '—'}</div>
                      </td>
                      <td className="secondary" style={{ whiteSpace: 'normal', maxWidth: 320 }}>
                        {item.rationale}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
